<template>
  <wizard
    first-step="personalDetails"
    class="wizard-request-tikkie"
  >
    <wizard-step
      :i18n="i18n"
      name="personalDetails"
      :model="formData"
      next-step="requestTikkie"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.personalDetails.title }}</h2>
        <slot name="personal-details-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <FormulateInput
          name="bonusCode"
          type="text"
          label="bonus code"
          validation="nohtml"
          class="custom-form-element"
        />
        <FormulateInput
          name="website"
          type="text"
          label="website"
          validation="nohtml"
          class="custom-form-element"
        />
        <FormulateInput
          name="email"
          type="email"
          :label="i18n.form.labels.email"
          validation="bail|required:trim|email"
          :validation-messages="i18n.form.validations.email"
        />

        <FormulateInput
          type="text"
          name="debtorNumber"
          :label="i18n.form.labels.debtorNumber"
          :help="i18n.form.help.debtorNumber"
          validation="bail|required:trim|max:9,length|min:6,length|nohtml|matches:/^[0-9\-]+$/"
          validation-name="Klantnummer"
          :validation-messages="i18n.form.validations.debtorNumber"
          @keydown.space.prevent
        />
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="requestTikkie"
      :model="formData"
      next-step="review"
      previous-step="personalDetails"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.requestTikkie.title }}</h2>
        <slot name="request-tikkie-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <FormulateInput
          name="whichInvoices"
          :options="i18n.form.labels.whichInvoicesOptions"
          type="radio"
          :label="i18n.form.labels.whichInvoices"
          validation="required"
          :validation-messages="i18n.form.validations.whichInvoices"
        />

        <FormulateInput
          v-if="customerWantsToPayForOneSpecificInvoice"
          type="text"
          name="invoiceNumber"
          :label="i18n.form.labels.invoiceNumber"
          :help="i18n.form.help.invoiceNumber"
          validation="bail|required:trim|number|min:6,length|max:10,length"
          :validation-messages="i18n.form.validations.invoiceNumber"
        />
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="review"
      :model="formData"
      :next-step="sendRequest"
      previous-step="requestTikkie"
      :submit-button-text="i18n.form.buttons.submit"
      :is-submitting="request.isSending"
      :error-submitting="request.errorSending"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.review.title }}</h2>
        <slot name="review-paragraph" />
      </wizard-step-intro>

      <table class="wizard-summary">
        <tbody>
          <tr>
            <td>{{ i18n.form.labels.reviewEmail }}</td>
            <td>{{ resultingData.personalDetails.email }}</td>
          </tr>
          <tr>
            <td>{{ i18n.form.labels.reviewCustomerNumber }}</td>
            <td>{{ resultingData.personalDetails.debtorNumber }}</td>
          </tr>

          <tr>
            <td colspan="2">
              {{ '\xa0' }}
            </td>
          </tr>

          <tr>
            <td>{{ i18n.form.labels.whichInvoices }}</td>
            <td>{{ translationOfSelectedWhichInvoices }}</td>
          </tr>
          <tr v-if="customerWantsToPayForOneSpecificInvoice">
            <td>{{ i18n.form.labels.invoiceNumber }}</td>
            <td>{{ resultingData.requestTikkie.invoiceNumber }}</td>
          </tr>
        </tbody>
      </table>
    </wizard-step>
  </wizard>
</template>

<script>
import Wizard from '@/components/wizards/components/Wizard.vue';
import WizardStep from '@/components/wizards/components/WizardStep.vue';
import WizardStepIntro from '@/components/wizards/components/WizardStepIntro.vue';
import axios from 'axios';
import getQueryParams from '@/lib/formulate/getFormQueryParam';

export default {
  name: 'KfWizardRequestTikkie',
  components: {
    Wizard,
    WizardStep,
    WizardStepIntro,
  },
  props: {
    i18n: {
      type: Object,
      default: null,
    },
    prefillData: {
      type: Object,
      default: () => { },
    },
    locale: {
      type: String,
      default: () => 'nl',
    },
    label: {
      type: String,
      default: 'Partou',
    },
    successPageUrl: {
      type: String,
      default: () => '/',
    },
  },
  data() {
    return {
      formData: {
        personalDetails: {},
        requestTikkie: {},
      },
      request: {
        isSending: false,
        errorSending: false,
      },
    };
  },
  computed: {
    customerWantsToPayForOneSpecificInvoice() {
      return this.formData.requestTikkie?.whichInvoices === 'one';
    },
    translationOfSelectedWhichInvoices() {
      return this.i18n.form.labels.whichInvoicesOptions[this.formData.requestTikkie.whichInvoices];
    },
    resultingData() {
      return {
        label: this.label,
        locale: this.locale,
        personalDetails: this.formData.personalDetails,
        requestTikkie: this.formData.requestTikkie,
      };
    },
  },
  created () {
    this.formData.personalDetails = {
      ...this.formData.personalDetails,
      ...getQueryParams(this.prefillData)
    }
  },
  methods: {
    sendRequest() {
      this.request.errorSending = false;
      this.request.isSending = true;

      const requestVerificationToken = document.body.querySelector('input[type=hidden][name=__RequestVerificationToken]').value;

      const config = {
        headers: {
          'X-REQUEST-VERIFICATION-TOKEN': requestVerificationToken,
        }
      };

      axios
        .post('/umbraco/api/wizard/requesttikkie', this.resultingData, config)
        .then(() => {
          window.location.href = this.successPageUrl;
        })
        .catch(() => {
          this.request.isSending = false;
          this.request.errorSending = true;
        });
    },
  },
};
</script>
